// FILE LOCATION: src/utils/fileSystemHelper.js
// File System Access API utilities for saving files

/**
 * Save multiple files to a folder using File System Access API
 * @param {FileSystemDirectoryHandle} folderHandle - The folder to save to
 * @param {Array} files - Array of {name, content} objects
 */
export async function saveFilesToFolder(folderHandle, files) {
  try {
    // Create a subfolder called "course-pages" to keep things organized
    const coursePagesHandle = await folderHandle.getDirectoryHandle('course-pages', { 
      create: true 
    });

    // Save each file
    for (const file of files) {
      await saveFile(coursePagesHandle, file.name, file.content);
    }

    return { success: true, count: files.length };
  } catch (error) {
    console.error('Error saving files:', error);
    throw new Error(`Failed to save files: ${error.message}`);
  }
}

/**
 * Save extracted images to an images folder using File System Access API
 * @param {FileSystemDirectoryHandle} folderHandle - The root folder handle
 * @param {Array} images - Array of {name, data, contentType} objects
 */
export async function saveImagesToFolder(folderHandle, images) {
  try {
    // Create an "images" directory at the root level (sibling to course-pages)
    const imagesHandle = await folderHandle.getDirectoryHandle('images', {
      create: true
    });

    for (const img of images) {
      const fileHandle = await imagesHandle.getFileHandle(img.name, { create: true });
      const writable = await fileHandle.createWritable();
      
      // Convert base64 data to a Blob for binary writing
      const res = await fetch(`data:${img.contentType};base64,${img.data}`);
      const blob = await res.blob();
      
      await writable.write(blob);
      await writable.close();
    }

    return { success: true, count: images.length };
  } catch (error) {
    console.error('Error saving images:', error);
    throw new Error(`Failed to save images: ${error.message}`);
  }
}

/**
 * Save a single file
 * @param {FileSystemDirectoryHandle} dirHandle - Directory to save to
 * @param {string} fileName - Name of the file
 * @param {string} content - File content
 */
async function saveFile(dirHandle, fileName, content) {
  try {
    // Get file handle (creates if doesn't exist)
    const fileHandle = await dirHandle.getFileHandle(fileName, { create: true });
    
    // Create a writable stream
    const writable = await fileHandle.createWritable();
    
    // Write content
    await writable.write(content);
    
    // Close the file
    await writable.close();
    
    return { success: true };
  } catch (error) {
    console.error(`Error saving file ${fileName}:`, error);
    throw error;
  }
}

/**
 * Check if File System Access API is supported
 * @returns {boolean}
 */
export function isFileSystemAccessSupported() {
  return 'showDirectoryPicker' in window;
}

/**
 * Get permission status for a directory
 * @param {FileSystemDirectoryHandle} handle
 * @returns {Promise<string>} - 'granted', 'denied', or 'prompt'
 */
export async function getPermissionStatus(handle) {
  try {
    const options = { mode: 'readwrite' };
    
    // Check current permission state
    const permission = await handle.queryPermission(options);
    
    return permission;
  } catch (error) {
    console.error('Error checking permission:', error);
    return 'denied';
  }
}

/**
 * Request permission for a directory
 * @param {FileSystemDirectoryHandle} handle
 * @returns {Promise<string>} - 'granted' or 'denied'
 */
export async function requestPermission(handle) {
  try {
    const options = { mode: 'readwrite' };
    
    // Request permission
    const permission = await handle.requestPermission(options);
    
    return permission;
  } catch (error) {
    console.error('Error requesting permission:', error);
    return 'denied';
  }
}

export async function saveZipToFolder(folderHandle, zipBlob, zipFileName) {
  try {
    const fileHandle = await folderHandle.getFileHandle(zipFileName, { create: true });
    const writable = await fileHandle.createWritable();
    await writable.write(zipBlob);
    await writable.close();
    return { success: true };
  } catch (error) {
    console.error('Error saving zip:', error);
    throw new Error(`Failed to save zip package: ${error.message}`);
  }
}